/**
 * CRMTasksPage — Task operatori (CRM-2, feb 2026).
 * Vista My Tasks default + toggle "Tutti" admin. Raggruppamento per scadenza.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, ListChecks, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITA_COLOR = {
  urgente: "bg-rose-100 text-rose-700 border-rose-300",
  alta: "bg-amber-100 text-amber-700 border-amber-300",
  media: "bg-sky-100 text-sky-700 border-sky-200",
  bassa: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

export default function CRMTasksPage() {
  const [showAll, setShowAll] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = showAll
        ? `${API}/api/admin/crm/tasks?per_page=200`
        : `${API}/api/admin/users/me/tasks`;
      const r = await axios.get(url, { withCredentials: true });
      setItems(r.data.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [showAll]);

  useEffect(() => { load(); }, [load]);

  // Group by bucket di scadenza
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const groups = {
    "Oggi": [], "Domani": [], "Questa settimana": [], "Settimana prossima": [], "Più avanti": [],
  };
  items.forEach((t) => {
    if (!t.scadenza) { groups["Più avanti"].push(t); return; }
    const d = new Date(t.scadenza); d.setHours(0, 0, 0, 0);
    const diff = Math.floor((d - today) / 86400000);
    if (diff <= 0) groups["Oggi"].push(t);
    else if (diff === 1) groups["Domani"].push(t);
    else if (diff <= 7) groups["Questa settimana"].push(t);
    else if (diff <= 14) groups["Settimana prossima"].push(t);
    else groups["Più avanti"].push(t);
  });

  return (
    <div className="p-4 md:p-6" data-testid="crm-tasks-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2"><ListChecks className="w-5 h-5" /> Task operatori</h2>
          <p className="text-xs text-zinc-500">Promemoria + rinnovi · auto-creati da scheduler · raggruppati per scadenza</p>
        </div>
        <div className="flex items-center gap-3">
        <a href={`${API}/api/admin/crm/export/tasks?format=xlsx`} target="_blank" rel="noreferrer"
           data-testid="crm-tasks-export-btn"
           className="px-3 py-1.5 text-xs rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200">Export</a>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid="crm-tasks-show-all" />
          Mostra tutti (admin)
        </label>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : items.length === 0 ? (
        <p className="text-center py-8 text-zinc-400 italic">Nessun task attivo</p>
      ) : (
        <div className="space-y-4">
          {Object.entries(groups).map(([label, list]) => list.length > 0 && (
            <div key={label} data-testid={`crm-tasks-group-${label}`}>
              <h3 className="text-xs uppercase text-zinc-500 mb-2 tracking-wider">{label} ({list.length})</h3>
              <div className="space-y-1.5">
                {list.map((t) => (
                  <div key={t.id} onClick={() => setSelectedId(t.id)}
                       className="border border-zinc-200 rounded p-2.5 hover:bg-zinc-50 cursor-pointer flex items-start justify-between gap-2"
                       data-testid={`crm-task-${t.id}`}>
                    <div className="flex-1">
                      <p className="text-sm font-medium flex items-center gap-1.5">
                        <span className={`px-1.5 py-0.5 rounded border text-[10px] ${PRIORITA_COLOR[t.priorita] || ""}`}>{t.priorita}</span>
                        {t.titolo}
                      </p>
                      <p className="text-xs text-zinc-500">{t.tipo} · scadenza {t.scadenza?.slice(0, 10)}{showAll && t.assegnato_a_user_id ? ` · ${t.assegnato_a_user_id}` : ""}</p>
                    </div>
                    <span className="text-[10px] uppercase text-zinc-400">{t.stato}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedId && <TaskDrawer id={selectedId} onClose={() => { setSelectedId(null); load(); }} />}
    </div>
  );
}

function TaskDrawer({ id, onClose }) {
  const [data, setData] = useState(null);
  const [stato, setStato] = useState("aperto");
  const [nota, setNota] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/admin/crm/tasks/${id}`, { withCredentials: true }).then((r) => {
      setData(r.data.task);
      setStato(r.data.task.stato);
    });
  }, [id]);

  const save = async () => {
    const body = { stato };
    if (nota) body.nota = nota;
    await axios.patch(`${API}/api/admin/crm/tasks/${id}`, body, { withCredentials: true });
    onClose();
  };

  const complete = async () => {
    await axios.patch(`${API}/api/admin/crm/tasks/${id}`, { stato: "completato" }, { withCredentials: true });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center md:justify-end" onClick={onClose} data-testid="crm-task-drawer">
      <div className="bg-white w-full md:max-w-lg md:h-full overflow-y-auto rounded-t-2xl md:rounded-t-none md:rounded-l-2xl p-5" onClick={(e) => e.stopPropagation()}>
        {!data ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <>
            <h3 className="text-lg font-semibold mb-1">{data.titolo}</h3>
            <p className="text-xs text-zinc-500 mb-2">{data.tipo} · {data.priorita} · scade {data.scadenza?.slice(0, 10)}</p>
            {data.descrizione && <p className="text-sm mb-3">{data.descrizione}</p>}
            <p className="text-xs text-zinc-500 mb-2">Assegnato a: <strong>{data.assegnato_a_user_id}</strong></p>
            {data.completato_at && (
              <p className="text-xs text-emerald-700 mb-2 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Completato {data.completato_at?.slice(0, 16).replace("T", " ")} da {data.completato_da_user_id}</p>
            )}

            <label className="block mb-2">
              <span className="text-xs text-zinc-500">Stato</span>
              <select value={stato} onChange={(e) => setStato(e.target.value)}
                className="w-full mt-1 px-3 py-2 text-sm rounded border border-zinc-200"
                data-testid="crm-task-stato-select">
                <option value="aperto">aperto</option>
                <option value="in_lavorazione">in_lavorazione</option>
                <option value="completato">completato</option>
                <option value="annullato">annullato</option>
              </select>
            </label>
            <label className="block mb-3">
              <span className="text-xs text-zinc-500">Nota</span>
              <textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={2}
                className="w-full mt-1 px-3 py-2 text-sm rounded border border-zinc-200" />
            </label>

            {data.note?.length > 0 && (
              <div className="border-t border-zinc-100 pt-2 mb-3">
                <p className="text-xs text-zinc-500 mb-1">Storico note</p>
                {data.note.map((n, i) => (
                  <div key={i} className="text-xs border border-zinc-100 rounded p-1.5 mb-1">
                    <p className="text-zinc-500">{n.ts?.slice(0, 16).replace("T", " ")} · {n.user_id}</p>
                    <p>{n.content}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <button onClick={onClose} className="flex-1 py-2 text-sm rounded bg-zinc-100">Annulla</button>
              <button onClick={save} className="flex-1 py-2 text-sm rounded bg-zinc-900 text-white" data-testid="crm-task-save-btn">Salva</button>
              {data.stato !== "completato" && (
                <button onClick={complete} className="px-3 py-2 text-sm rounded bg-emerald-600 text-white flex items-center gap-1" data-testid="crm-task-complete-btn">
                  <CheckCircle2 className="w-3 h-3" /> Completa
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
